import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  RadioGroup,
  List,
  ListItem,
  Radio,
  FormControl
} from '@mui/material';
import { radioClasses } from '@mui/joy/Radio';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

/**
 * DatasetGroupSelector - Two-row selector: datasets on top, groups below
 *
 * @param {Array} datasets - Array of dataset objects with metadata.groups
 * @param {string} selectedDatasetId - Currently selected dataset ID (optional, for edit mode)
 * @param {string} selectedGroup - Currently selected group code
 * @param {function} onChange - Callback when selection changes: (datasetId, groupCode) => void
 * @param {number} iconSize - Size of icons in pixels (default: 40)
 * @param {boolean} disabled - Whether selector is disabled
 * @param {boolean} showLabel - Whether to show label (default: true)
 */
export default function DatasetGroupSelector({
  datasets,
  selectedDatasetId,
  selectedGroup,
  onChange,
  iconSize = 40,
  disabled = false,
  showLabel = true
}) {
  console.log('[DatasetGroupSelector] Props received:', {
    selectedDatasetId,
    selectedGroup,
    datasets: datasets?.map(d => d.dataset_id)
  });

  const [selectedDataset, setSelectedDataset] = useState(selectedDatasetId || null);
  // Remember group selection per dataset: { datasetId: groupCode }
  const [groupsByDataset, setGroupsByDataset] = useState({});

  // Initialize/update selected dataset from prop (edit mode)
  useEffect(() => {
    console.log('[DatasetGroupSelector] selectedDatasetId changed to:', selectedDatasetId);
    if (selectedDatasetId) {
      setSelectedDataset(selectedDatasetId);
      // If selectedGroup is provided, remember it for this dataset
      if (selectedGroup) {
        setGroupsByDataset(prev => ({
          ...prev,
          [selectedDatasetId]: selectedGroup
        }));
      }
    }
  }, [selectedDatasetId, selectedGroup]);

  // Auto-select first dataset if none selected (create mode)
  // Only run this after initial render to avoid overwriting props
  const [isInitialized, setIsInitialized] = React.useState(false);

  useEffect(() => {
    if (!isInitialized) {
      setIsInitialized(true);
      return;
    }

    if (!selectedDataset && !selectedDatasetId && datasets && datasets.length > 0) {
      const firstDatasetId = datasets[0].dataset_id;
      console.log('[DatasetGroupSelector] Auto-selecting first dataset:', firstDatasetId);
      setSelectedDataset(firstDatasetId);
      // Notify parent of auto-selected dataset
      onChange(firstDatasetId, null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasets, selectedDataset, selectedDatasetId, isInitialized]);

  // Get currently selected group (from memory or prop)
  const currentSelectedGroup = groupsByDataset[selectedDataset] || selectedGroup;

  // Get groups for selected dataset
  const availableGroups = useMemo(() => {
    if (!selectedDataset || !datasets) return [];
    const dataset = datasets.find(ds => ds.dataset_id === selectedDataset);
    return dataset?.metadata?.groups || [];
  }, [selectedDataset, datasets]);

  if (!datasets || datasets.length === 0) {
    return null;
  }

  const handleDatasetClick = (datasetId) => {
    if (disabled) return;
    setSelectedDataset(datasetId);

    // Get groups for this dataset
    const dataset = datasets.find(ds => ds.dataset_id === datasetId);
    const groups = dataset?.metadata?.groups || [];

    // Restore previously selected group, or auto-select first group if none remembered
    let groupToSelect = groupsByDataset[datasetId];
    if (!groupToSelect && groups.length > 0) {
      groupToSelect = groups[0].code;
      // Remember this auto-selection
      setGroupsByDataset(prev => ({
        ...prev,
        [datasetId]: groupToSelect
      }));
    }

    onChange(datasetId, groupToSelect || null);
  };

  const handleGroupClick = (groupCode) => {
    if (disabled) return;
    // Cannot deselect - clicking same group does nothing
    if (currentSelectedGroup === groupCode) return;

    // Remember group selection for current dataset
    setGroupsByDataset(prev => ({
      ...prev,
      [selectedDataset]: groupCode
    }));

    onChange(selectedDataset, groupCode);
  };

  const getIconUrl = (iconUrl) => {
    // Backend now returns full icon_url (converted from S3 key)
    return iconUrl || undefined;
  };

  return (
    <Box sx={{ mb: 1 }}>
      {showLabel && (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 600,
            mb: 0.5,
            fontSize: '0.8rem',
            color: 'text.secondary'
          }}
        >
          Group
        </Typography>
      )}

      {/* Row 1: Datasets */}
      <Box sx={{ mb: 1.5 }}>
        {showLabel && (
          <Typography
            variant="caption"
            sx={{
              fontSize: '0.7rem',
              color: 'text.disabled',
              mb: 0.5,
              display: 'block'
            }}
          >
            Dataset
          </Typography>
        )}
        <FormControl disabled={disabled} fullWidth>
          <RadioGroup value={selectedDataset || ''} sx={{ flexDirection: 'row', gap: 1 }}>
            <List
              orientation="horizontal"
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
                p: 0
              }}
            >
              {datasets.map((dataset) => {
                const isSelected = selectedDataset === dataset.dataset_id;
                // Backend returns icon_url (converted from icon_s3_key)
                const iconUrl = getIconUrl(dataset.icon_url);

                return (
                  <ListItem
                    key={dataset.dataset_id}
                    variant="outlined"
                    onClick={() => handleDatasetClick(dataset.dataset_id)}
                    sx={{
                      width: iconSize,
                      height: iconSize,
                      boxShadow: 'sm',
                      bgcolor: 'background.paper',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      opacity: disabled ? 0.5 : 1,
                      border: isSelected ? '3px solid' : '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      borderRadius: 1,
                      p: 0.5,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      '&:hover': {
                        borderColor: disabled ? 'divider' : 'primary.light',
                        bgcolor: disabled ? 'background.paper' : 'action.hover'
                      }
                    }}
                  >
                    {iconUrl ? (
                      <img
                        src={iconUrl}
                        alt={dataset.dataset_id}
                        style={{
                          width: iconSize - 10,
                          height: iconSize - 10,
                          objectFit: 'contain'
                        }}
                      />
                    ) : (
                      <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center' }}>
                        {dataset.dataset_id}
                      </Typography>
                    )}

                    {isSelected && (
                      <CheckCircleRoundedIcon
                        sx={{
                          position: 'absolute',
                          top: -8,
                          right: -8,
                          fontSize: 20,
                          color: 'primary.main',
                          bgcolor: 'background.paper',
                          borderRadius: '50%'
                        }}
                      />
                    )}

                    <Radio
                      value={dataset.dataset_id}
                      checked={isSelected}
                      disabled={disabled}
                      sx={{ display: 'none' }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </RadioGroup>
        </FormControl>
      </Box>

      {/* Row 2: Groups for selected dataset */}
      {availableGroups.length > 0 && (
        <Box>
          {showLabel && (
            <Typography
              variant="caption"
              sx={{
                fontSize: '0.7rem',
                color: 'text.disabled',
                mb: 0.5,
                display: 'block'
              }}
            >
              Select Group
            </Typography>
          )}
          <FormControl disabled={disabled} fullWidth>
            <RadioGroup value={currentSelectedGroup || ''} sx={{ flexDirection: 'row', gap: 1 }}>
              <List
                orientation="horizontal"
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 1.5,
                  p: 0
                }}
              >
                {availableGroups.map((group) => {
                  const isSelected = currentSelectedGroup === group.code;
                  // Backend returns icon_url (converted from icon_s3_key)
                  const iconUrl = getIconUrl(group.icon_url);
                  console.log(`[DatasetGroupSelector] Group ${group.code}: icon_url="${group.icon_url}", iconUrl="${iconUrl}"`);

                  return (
                    <ListItem
                      key={group.code}
                      variant="outlined"
                      onClick={() => handleGroupClick(group.code)}
                      sx={{
                        width: iconSize,
                        height: iconSize,
                        boxShadow: 'sm',
                        bgcolor: 'background.paper',
                        cursor: disabled ? 'not-allowed' : 'pointer',
                        opacity: disabled ? 0.5 : 1,
                        border: isSelected ? '3px solid' : '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderRadius: 1,
                        p: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        position: 'relative',
                        '&:hover': {
                          borderColor: disabled ? 'divider' : 'primary.light',
                          bgcolor: disabled ? 'background.paper' : 'action.hover'
                        }
                      }}
                    >
                      {iconUrl ? (
                        <img
                          src={iconUrl}
                          alt={group.label || group.code}
                          title={group.label || group.code}
                          style={{
                            width: iconSize - 10,
                            height: iconSize - 10,
                            objectFit: 'contain'
                          }}
                        />
                      ) : (
                        <Typography variant="caption" sx={{ fontSize: '0.65rem', textAlign: 'center' }}>
                          {group.code}
                        </Typography>
                      )}

                      {isSelected && (
                        <CheckCircleRoundedIcon
                          sx={{
                            position: 'absolute',
                            top: -8,
                            right: -8,
                            fontSize: 20,
                            color: 'primary.main',
                            bgcolor: 'background.paper',
                            borderRadius: '50%'
                          }}
                        />
                      )}

                      <Radio
                        value={group.code}
                        checked={isSelected}
                        disabled={disabled}
                        sx={{ display: 'none' }}
                      />
                    </ListItem>
                  );
                })}
              </List>
            </RadioGroup>
          </FormControl>
        </Box>
      )}
    </Box>
  );
}
